import { useEffect, useState } from 'react';
import { getAlerts } from '@/api/dashboard-live';
import type { DashboardAlert } from '@/api/dashboard-live';
import { usePilotage } from '../context';

import { DailyChart, Legend, TimeChart } from '../charts';
import { collectDetail, opsDetail } from '../details';
import { AlertList, Panel } from '../ui';
import { num } from '../format';
import { STATUS } from '../theme';

/** Écran 6 — Alertes ouvertes + historique (7 jours jusqu'au jour affiché) */
export function AlertsScreen() {
  const { payload, snapshot, isLive, day, openDetail } = usePilotage();
  const [history, setHistory] = useState<DashboardAlert[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAlerts(7, isLive ? undefined : day)
      .then((r) => !cancelled && setHistory(r.history))
      .catch(() => !cancelled && setHistory([]));
    return () => {
      cancelled = true;
    };
  }, [isLive, day, snapshot.id]);

  const sevDefs = [
    { metric: 'alerts.critical', label: 'Critiques', color: STATUS.critical },
    { metric: 'alerts.warning', label: 'Attention', color: STATUS.warning },
  ];

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-3 xl:grid-cols-12 xl:grid-rows-[minmax(0,1fr)_minmax(0,1fr)]">
      <Panel title={isLive ? 'Alertes ouvertes' : 'Alertes ouvertes en fin de journée'} className="xl:col-span-5 xl:row-span-2" bodyClassName="overflow-y-auto" right={<span className="text-xs text-slate-400">{num(payload.alerts.length)}</span>}>
        <AlertList alerts={payload.alerts} />
      </Panel>
      <Panel title="Alertes actives (24 h)" className="xl:col-span-4" right={<Legend defs={sevDefs} />}>
        <TimeChart defs={sevDefs} height="100%" />
      </Panel>
      <Panel title="Collecte" className="xl:col-span-3" onOpen={() => openDetail(collectDetail(payload, snapshot.capturedAt))}>
        <div className="space-y-1 text-sm">
          {Object.entries(payload.sections).map(([k, s]) => (
            <div key={k} className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">{k}</span>
              <span className={s?.status === 'error' ? 'text-red-600' : 'text-slate-900 dark:text-slate-100'}>{s?.status === 'ok' ? (s.reused ? 'réutilisée' : 'OK') : s?.status}</span>
            </div>
          ))}
          <button type="button" className="mt-2 text-xs text-slate-400 underline" onClick={(e) => { e.stopPropagation(); openDetail(opsDetail(payload)); }}>
            Exploitation ADM
          </button>
        </div>
      </Panel>
      <Panel title="Historique des alertes (7 jours)" className="xl:col-span-4" bodyClassName="overflow-y-auto">
        {history === null ? <p className="text-sm text-slate-400">Chargement…</p> : <AlertList alerts={history} showResolved empty="Aucune alerte sur la période" />}
      </Panel>
      <Panel title="Alertes par jour" subtitle="alertes actives à la clôture" className="xl:col-span-3">
        <DailyChart defs={[{ metric: 'alerts.warning', label: 'Alertes attention', color: STATUS.warning }, { metric: 'alerts.critical', label: 'Alertes critiques', color: STATUS.critical }]} bars height="100%" days={30} />
      </Panel>
    </div>
  );
}
